<template>
    <div class="md map">
        <div class="caption">
            <div class="essay">
                <a>{{ caption }}</a>
                <cpLabel
                    v-if="minorCaption"
                    themes="minor"
                    :text="minorCaption"
                ></cpLabel>
            </div>
            <ul class="func">
                <li class="is-status">{{ scale }}x</li>
                <li v-if="scale < 10"><cpBtn
                    themes="info"
                    icon="zoom_in"
                    text="放大"
                    @click.stop="zoomIn"
                ></cpBtn></li>
                <li v-else><cpBtn
                    icon="zoom_in"
                    text="放大"
                    :disabled="true"
                ></cpBtn></li>
                <li v-if="scale === 1"><cpBtn
                    icon="zoom_out"
                    text="縮小"
                    :disabled="true"
                ></cpBtn></li>
                <li v-else><cpBtn
                    themes="info"
                    icon="zoom_out"
                    text="縮小"
                    @click.stop="zoomOut"
                ></cpBtn></li>
                <li><cpBtn
                    icon="gps_fixed"
                    text="重新定位"
                    @click.stop="initMap"
                ></cpBtn></li>
            </ul>
        </div>
        <slot></slot>
        <div class="interval">
            <div
                class="proportion"
            >
                <div
                    class="content"
                    id="d3Map"
                    ref="map"
                ></div>
            </div>
        </div>
    </div>
</template>

<script>
    import cpBtn from '@/components/cpBtn'
    import cpLabel from '@/components/cpLabel'

    import { mapMutations } from 'vuex'

    export default {
        components: {
            cpBtn,
            cpLabel,
        },
        props: {
            geojson: {
                type: Object,
                required: true
            },
            caption: {
                type: String,
                required: false,
                default: '資料'
            },
            minorCaption: {
                type: String,
                required: false,
                default: ''
            },
            initSet: {
                type: Object,
                required: false,
                default: function(){
                    return {}
                }
            }
        },
        data: function(){
            let _id = 'map'+ this.$okaTool.getUUID();

            let $initSet = this.initSet,
                _scale = $initSet.scale || 1,
                _dotImg = $initSet.dotImg || null;

            return {
                id: _id,
                width: 0,
                height: 0,
                scale: _scale, //縮放的比例
                area: [[0, 0], [0, 0]], //用來算 scaleBase 的經緯度大小極限
                scaleBase: 0, //縮放的 base
                projection: null, //投影法
                path: null, //路徑
                init: false, //是否被初始化過
                dotImg: _dotImg, //'http://i.imgur.com/7Nlcay7.jpg'
                properties: { //default 的地圖樣式
                    style:{
                        fill: 'rgba(245, 246, 247, .7)',
                        stroke: '#555555',
                        strokeWidth: 0.2
                    },
                    hoverStyle: {
                        strokeWidth: 1
                    }
                },
                dotProperties: { //default 的地圖樣式
                    style:{
                        r: '5px',
                        fill: '#cc4400',
                        stroke: 'transparent',
                        strokeWidth: 0,
                        opacity: 0.6
                    },
                    hoverStyle: {
                        fill: '#802a00',
                        stroke: 'transparent',
                        opacity: 1
                    }
                }
            }
        },
        watch: {
            geojson: {
                handler: function(){

                    if( this.geojson.type ) {
                        this.computedMax();
                        this.updateMap();
                    }
                },
                deep: true
            }
        },
        methods: {
            ...mapMutations([
                'isLoadingChange',
                'globalResizeFuncQueuePush',
                'globalResizeFuncQueueClear'
            ]),
            updateZoom: function(scale){
                this.$set(this, 'scale', scale);

                this.$d3.select('#d3Map svg').style('transform', 'scale('+ scale +')');
            },
            zoomIn: function(){
                this.updateZoom(this.scale + 1);
            },
            zoomOut: function(){
                this.updateZoom(this.scale - 1);
            },
            initMap: function(){
                let $map = this.$d3.select('#d3Map');

                this.projection.translate([this.width / 2, this.height / 2]);

                $map.selectAll('path').attr('d', this.path);

                $map.selectAll('circle')
                    .attr("cx", (pos) => {
                        return this.projection(pos)[0];
                    })
                    .attr("cy", (pos) => {
                        return this.projection(pos)[1];
                    })

                let $initSet = this.initSet,
                    _scale = $initSet.scale || 1;

                this.updateZoom(_scale);
            },
            computedMax: function(){
                let $data = this.geojson.features,
                    $result = [[undefined, undefined], [undefined, undefined]];

                let $_setMinAndMax = (result, data) => { //找出 x, y 的最大值以及最小值

                    if( result[0][0] === undefined ) {
                        result[0][0] = data[0];
                    }else {
                        result[0][0] = Math.min(result[0][0], data[0])
                    }

                    if( result[0][1] === undefined ) {
                        result[0][1] = data[0];
                    }else {
                        result[0][1] = Math.max(result[0][1], data[0])
                    }

                    if( result[1][0] === undefined ) {
                        result[1][0] = data[1];
                    }else {
                        result[1][0] = Math.min(result[1][0], data[1])
                    }

                    if( result[1][1] === undefined ) {
                        result[1][1] = data[1];
                    }else {
                        result[1][1] = Math.max(result[1][1], data[1])
                    }
                }

                $data.forEach(geometry => {
                    let $geometry = geometry.geometry,
                        $coordinates = null;

                    if( $geometry && $geometry.coordinates ) {
                        $coordinates = $geometry.coordinates;

                        $coordinates.forEach(coordinate => {

                            if( coordinate[0] && coordinate[0][0] ) {

                                if( Array.isArray(coordinate[0][0]) ) {

                                    coordinate[0].forEach(coordinateNode => {
                                        $_setMinAndMax($result, coordinateNode);
                                    });
                                }else if( typeof coordinate[0][0] === 'number' ) {
                                    $_setMinAndMax($result, coordinate[0]);
                                }else {
                                    console.error(coordinate);
                                }
                            }
                        });
                    }
                });

                this.$set(this, 'area', $result);
            },
            computedWidth: function(){
                let $map = this.$refs.map,
                    _width = 0;

                if( $map && $map.offsetWidth ) {
                    _width = $map.offsetWidth;
                }

                this.$set(this, 'width', _width);
            },
            computedHeight: function(){
                let _longitude = Math.abs(this.area[0][0] - this.area[0][1]), //經度
                    _latitude = Math.abs(this.area[1][0] - this.area[1][1]); //緯度

                let _scale = _latitude / _longitude,
                    _width = this.width;

                this.$set(this, 'height', _width * _scale);
            },
            createMap: function(){
                let _longitude = (this.area[0][0] + this.area[0][1]) / 2, //經度
                    _latitude = (this.area[1][0] + this.area[1][1]) / 2; //緯度

                let $initSet = this.initSet;

                if( $initSet.longitude ) {
                    _longitude = $initSet.longitude;
                }

                if( $initSet.latitude ) {
                    _latitude = $initSet.latitude;
                }

                let _size = Math.abs(this.area[0][0] - this.area[0][1]); //經度

                this.$d3.select('#d3Map svg').remove();

                let _base = 50; //地圖畫在介面上，應該充滿所有寬度的倍數

                this.scaleBase = this.width / _size * _base;

                let $svg = this.$d3.select('#d3Map').append('svg').attr('height', this.height),
                    $g = $svg.append('g');

                if( this.dotImg ) {
                    let $filter = $svg.append('filter');

                    $filter.attr('x', '0%').attr('y', '0%')
                        .attr('height', '100%').attr('width', '100%')
                        .attr('id', 'ship').attr('patternUnits', 'userSpaceOnUse')
                        .append('feImage')
                        .attr('xlink:href', this.dotImg);
                }

                this.projection = this.$d3.geoEquirectangular()
                    .center([_longitude, _latitude])
                    .scale(this.scaleBase)
                    .translate([this.width / 2, this.height / 2]);

                this.path = this.$d3.geoPath().projection(this.projection);

                $g.call(this.$d3.drag().on('drag', () => { //註冊拖曳地圖方法
                    let currTranslate = this.projection.translate();

                    this.projection.translate([currTranslate[0] + this.$d3.event.dx, currTranslate[1] + this.$d3.event.dy]);

                    $g.selectAll('path')
                        .attr('d', this.path);

                    $g.selectAll('circle')
                        .attr('cx', (pos) => {
                            return this.projection(pos)[0];
                        })
                        .attr('cy', (pos) => {
                            return this.projection(pos)[1];
                        })
                }));

                let $_getOpt = (info, key, type) => {
                        let $default = this.properties[key] || {},
                            $clone = _.cloneDeep($default);

                        if( type === 'dot' ) {
                            $clone = this.dotProperties[key] || {};
                        }

                        let $opt = info.properties && info.properties[key];

                        if( $opt === undefined ) {
                            $opt = {};
                        }

                        let $result = this.$okaTool.extend({
                                target: $clone,
                                ref: $opt,
                                hard: true,
                                copy: true
                            });

                        return $result;
                    },
                    $_getStyle = (style) => {
                        let _result = '';

                        for( let _key in style ) {
                            let __key = _key.replace(/([A-Z])/g, '-$1');

                            __key = __key.toLowerCase();

                            _result = _result + __key +':'+ style[_key] +';';
                        }

                        return _result;
                    }

                $g.selectAll('path')
                    .data(this.geojson.features)
                    .enter()
                    .append('path')
                    .attr('d', this.path)
                    .attr('style', (info, index, paths) => {
                        let $style = $_getOpt(info, 'style', 'path');

                        return $_getStyle($style);
                    })
                    .on('mouseover', (info, index, paths) => {
                        let $event = $_getOpt(info, 'event', 'path'),
                            $style = $_getOpt(info, 'style', 'path'),
                            $hoverStyle = $_getOpt(info, 'hoverStyle', 'path');

                        $hoverStyle = this.$okaTool.extend({
                            target: $hoverStyle,
                            ref: $style,
                            copy: true
                        });

                        if( $event && $event.mouseover ) {
                            $event.mouseover(info.properties);
                        }

                        this.$d3.select(paths[index]).attr('style', $_getStyle($hoverStyle)); //逐一檢查設定，如果沒有設定就拿預設值
                    })
                    .on('mouseout', (info, index, paths) => {
                        let $event = $_getOpt(info, 'event', 'path'),
                            $style = $_getOpt(info, 'style', 'path');

                        if( $event && $event.mouseout ) {
                            $event.mouseout(info.properties);
                        }

                        this.$d3.select(paths[index]).attr('style', $_getStyle($style));
                    })
                    .on('click', (info, index, paths) => {
                        let $event = $_getOpt(info, 'event', 'path');

                        if( $event && $event.click ) {
                            $event.click(info.properties);
                        }
                    });

                if( this.geojson.dots ) { //如果有點點資料

                    let $circle = $g.selectAll('circle')
                        .data(this.geojson.dots)
                        .enter()
                        .append('circle')
                        .attr('cx', (pos) => {
                            return this.projection(pos)[0];
                        })
                        .attr('cy', (pos) => {
                            return this.projection(pos)[1];
                        });

                    $circle.attr('style', (info, index, paths) => {
                            let $style = $_getOpt(info, 'style', 'dot');

                            return $_getStyle($style);
                        })
                        .on('mouseover', (info, index, paths) => {
                            let $event = $_getOpt(info, 'event', 'dot'),
                                $style = $_getOpt(info, 'style', 'dot'),
                                $hoverStyle = $_getOpt(info, 'hoverStyle', 'dot');

                            $hoverStyle = this.$okaTool.extend({
                                target: $hoverStyle,
                                ref: $style,
                                copy: true
                            });

                            if( $event && $event.mouseover ) {
                                $event.mouseover(info);
                            }

                            this.$d3.select(paths[index]).attr('style', $_getStyle($hoverStyle)); //逐一檢查設定，如果沒有設定就拿預設值
                        })
                        .on('mouseout', (info, index, paths) => {
                            let $event = $_getOpt(info, 'event', 'dot'),
                                $style = $_getOpt(info, 'style', 'dot');

                            if( $event && $event.mouseout ) {
                                $event.mouseout(info);
                            }

                            this.$d3.select(paths[index]).attr('style', $_getStyle($style))
                        })
                        .on('click', (info, index, paths) => {
                            let $event = $_getOpt(info, 'event', 'dot');

                            if( $event && $event.click ) {
                                $event.click(info);
                            }
                        });

                        if( this.dotImg ) {
                            $circle.attr('filter', 'url(#ship)');
                        }
                    }
            },
            updateMap: function(){
                let _int = 1000; //第一次要延遲 1000 毫秒載入

                if( this.init ) {
                    _int = 0;
                }else {
                    this.init = true;
                    this.isLoadingChange(true);
                }

                let $timer = window.setTimeout(() => {
                        this.computedWidth(); //更新寬
                        this.computedHeight(); //更新高
                        this.createMap(); //根據新的寬高開始重繪
                        this.isLoadingChange(false);
                        this.updateZoom(this.scale);
                        window.clearTimeout($timer);
                    }, _int);
            }
        },
        created: function(){
            this.globalResizeFuncQueuePush({
                id: this.id,
                func: this.updateMap
            });
        },
        destroyed: function(){
            this.globalResizeFuncQueueClear(this.id);
        }
    }
</script>

<style scoped lang='scss'>

    .md.map::v-deep {
        padding: $interval-m;

        .caption {
            display: flex;

            .essay {
                flex: 1;
                display: flex;
                align-items: center;

                a {
                    color: $major-color;
                    font-size: $font-size-l;
                }
            }

            /*ul*/.func {
                @extend %reset;

                li {
                    display: inline-block;

                    &.is-status {
                        margin-right: $interval-m;
                    }
                }

                a {

                }
            }
        }

        .interval {
            margin-top: $interval-m;
        }

        .proportion {

        }

        .content {
            overflow: hidden;
        }

        circle {
            transform-origin: center;
        }

        svg {
            width: 100%;
            position: relative;
            transform-origin: center;
            transition: $transition;
        }
    }
</style>
